import { defineComponent, h, provide, ref, watch } from 'vue';
import { Modal } from '@/ui/components/Modal';
import { VNodeData } from 'vue/types/vnode.d';
import { emptyIssueTemplate, IssueTemplateEditable } from '@/ui/DTO';
import { loader, pageSubtitle } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions } from '@/ui/types';
import { Button } from '@/ui/components/Button';
import cloneDeep from 'lodash/cloneDeep';
import { CREATE_TEMPLATE_MODAL_WIDTH } from '@/ui/components/modals/constants';
import { createUserIdentifierFromActionPageKey } from '@/ui/injectionKeys';
import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
import IssueTemplateForm from '@/ui/components/issueTemplate/issueCreateTemplateModal/IssueTemplateForm.vue';
import { useIssueTemplate } from '@/ui/components/issueTemplate/useIssueTemplate';

interface IssueTemplateCreateModalProps {
  active: boolean;
  issueTemplate?: IssueTemplateEditable | null;
}

export const IssueTemplateCreateModal = defineComponent<IssueTemplateCreateModalProps>({
  name: 'IssueTemplateCreateModal',
  props: ['active', 'issueTemplate'],
  setup(props, context) {
    provide(createUserIdentifierFromActionPageKey, CreateUserModelIdentifier.actionCreator);

    const issueTemplateEditable = ref(props.issueTemplate ? cloneDeep(props.issueTemplate) : emptyIssueTemplate());

    const {
      validationErrors,
      hasNoValidationErrors,
      showValidationErrors,
      assigneeOptions,
      userActionApprovalOptions,
      getFilters,
      getUsers,
    } = useIssueTemplate(issueTemplateEditable);

    watch(() => props.active, (newValue, oldValue) => {
      //hide validation errors when reopening the modal
      if (!oldValue && newValue) {
        showValidationErrors.value = false;
      }
    });

    const closeModal = () => {
      issueTemplateEditable.value = emptyIssueTemplate();
      context.emit('close');
    };

    const onSaveClick = () => {
      showValidationErrors.value = true;

      if (hasNoValidationErrors.value) {
        context.emit('save', cloneDeep(issueTemplateEditable.value));

        issueTemplateEditable.value = emptyIssueTemplate();
        showValidationErrors.value = false;
      }
    };

    const onIssueTemplatePropChange = (resource: IssueTemplateEditable | null | undefined) => {
      issueTemplateEditable.value = cloneDeep(resource) || emptyIssueTemplate();
    };

    watch(() => props.issueTemplate, onIssueTemplatePropChange);

    return function render() {
      return h(Modal, {
        props: {
          ...props,
          width: CREATE_TEMPLATE_MODAL_WIDTH,
          ariaRole: 'dialog',
          trapFocus: true,
          destroyOnHide: false,
          ariaModal: true,
        },
        on: {
          close: closeModal,
        },
        scopedSlots: {
          actions: () => h('div', [
            h(Button, {
              props: { type: 'is-text', btnClass: 'mr-2 has-text-weight-semibold' },
              on: { click: closeModal },
            }, t('modal.cancel')),
            h(Button, {
              props: {
                loading: false,
                disabled: false,
              },
              on: { click: onSaveClick },
            }, t('common.save')),
          ]),
        },
      }, [
        loader('div', getFilters, [
          pageSubtitle(t('issue-template.issue')),
          h(IssueTemplateForm, {
            props: {
              record: issueTemplateEditable.value,
              userOptions: assigneeOptions.value as SelectOptions<string>,
              userApprovalOptions: userActionApprovalOptions.value as SelectOptions<string>,
              validationErrors: validationErrors.value,
            },
            on: {
              'update-user-options': () => getUsers.run(),
            },
          }),
        ]),
      ]);
    };
  },
});

export function issueTemplateCreateModal(data: VNodeData & { props: IssueTemplateCreateModalProps }) {
  return h(IssueTemplateCreateModal, data);
}
